import { useEffect, useMemo, useState } from "react";
import Select, { components } from "react-select";
import { getAllRoles } from "../../services/securityRoleService";

/**
 * Select de Roles (múltiple) con búsqueda local, dark-mode y sin paginación.
 *
 * Props:
 * - value: string[] (GUIDs seleccionados)
 * - onChange: (ids: string[]) => void
 * - isDisabled?: boolean
 * - placeholder?: string
 * - className?: string
 * - allOptions?: Array<{ value: string; label: string; raw?: any }>
 *      (Opcional: si ya traes todos los roles en el form, pásalos aquí y NO hará fetch)
 */
const SelectRolesMulti = ({
  value = [],
  onChange,
  isDisabled = false,
  placeholder = "Seleccione roles...",
  className,
  allOptions, // <- si viene, no hacemos fetch
}) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [options, setOptions] = useState([]); // { value, label, raw }
  const [loading, setLoading] = useState(false);

  // ------------------------------------------------------------
  // Detección de dark-mode dinámica
  // ------------------------------------------------------------
  useEffect(() => {
    const checkDark = () =>
      document.body.classList.contains("dark-mode") ||
      document.querySelector(".app")?.classList.contains("dark-mode");

    setIsDarkMode(checkDark());
    const node = document.querySelector(".app") || document.body;
    const obs = new MutationObserver(() => setIsDarkMode(checkDark()));
    obs.observe(node, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  // ------------------------------------------------------------
  // Carga de roles: desde props (allOptions) o desde el backend
  // ------------------------------------------------------------
  useEffect(() => {
    let mounted = true;

    const mapRoles = (roles) =>
      (roles || []).map((r) =>
        r.value && r.label
          ? r // ya viene mapeado desde el form
          : { value: r.id, label: r.name, raw: r }
      );

    if (allOptions && Array.isArray(allOptions)) {
      setOptions(mapRoles(allOptions));
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const roles = await getAllRoles();
        if (mounted) setOptions(mapRoles(roles));
      } catch (e) {
        console.error("Error cargando roles:", e);
        if (mounted) setOptions([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [allOptions]);

  // ------------------------------------------------------------
  // Construir la selección actual a partir de los IDs
  // ------------------------------------------------------------
  const selectedOptions = useMemo(() => {
    if (!Array.isArray(value)) return [];
    const byId = new Map(options.map((o) => [o.value, o]));
    return value.map((id) => byId.get(id)).filter(Boolean);
  }, [value, options]);

  // ------------------------------------------------------------
  // Estilos del react-select (incluye dark-mode)
  // ------------------------------------------------------------
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: 42,
      backgroundColor: isDarkMode ? "#1e1e2f" : "#fff",
      borderColor: state.isFocused
        ? "#22c55e"
        : isDarkMode
        ? "#444"
        : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(34,197,94,.25)" : "none",
      color: isDarkMode ? "#fff" : "#000",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? "#1e1e2f" : "#fff",
      color: isDarkMode ? "#fff" : "#000",
      zIndex: 20000,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 20000,
    }),
    multiValue: (base) => ({
      ...base,
      background: isDarkMode ? "#18362a" : "#e8f7ef",
      border: `1px solid ${isDarkMode ? "#1f9d5c" : "#22c55e"}`,
      paddingInline: 2,
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: isDarkMode ? "#a7f3d0" : "#065f46",
      fontWeight: 600,
      paddingTop: 4,
      paddingBottom: 4,
      display: "flex",
      alignItems: "center",
    }),
    multiValueRemove: (base) => ({
      ...base,
      ":hover": { background: "#ef4444", color: "#fff" },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#22c55e"
        : state.isFocused
        ? isDarkMode
          ? "#2c2c3e"
          : "#f0fdf4"
        : isDarkMode
        ? "#1e1e2f"
        : "#fff",
      color: state.isSelected ? "#fff" : isDarkMode ? "#fff" : "#000",
      cursor: "pointer",
      paddingTop: 10,
      paddingBottom: 10,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDarkMode ? "#aaa" : "#888",
    }),
    input: (provided) => ({
      ...provided,
      color: isDarkMode ? "#fff" : "#000",
    }),
  };

  // ------------------------------------------------------------
  // Opción del menú (dropdown): nombre + descripción + #permisos
  // ------------------------------------------------------------
  const Option = (props) => {
    const role = props.data.raw;
    const permCount = role?.permissionDescriptions?.length ?? 0;
    return (
      <components.Option {...props}>
        <div className="d-flex flex-column">
          <div className="d-flex align-items-center justify-content-between">
            <span className="fw-semibold">
              {role?.name || props.data.label}
            </span>
            {permCount > 0 && (
              <span
                className="badge rounded-pill"
                style={{
                  background: "#ecfdf5",
                  color: "#065f46",
                  border: "1px solid #22c55e",
                }}
              >
                {permCount} permisos
              </span>
            )}
          </div>
          {role?.description && (
            <small className="text-muted mt-1">{role.description}</small>
          )}
        </div>
      </components.Option>
    );
  };

  // ------------------------------------------------------------
  // Etiqueta de cada pastilla seleccionada:
  //   Nombre del rol (línea 1)
  //   Descripción del rol (línea 2, más pequeña)
  // ------------------------------------------------------------
  const MultiValueLabel = (props) => {
    const role = props.data.raw;
    const name = role?.name || props.data.label;
    const description = role?.description;

    return (
      <components.MultiValueLabel {...props}>
        <div className="d-flex flex-column">
          <span>{name}</span>
          {description && (
            <small
              style={{
                fontSize: "0.7rem",
                opacity: 0.8,
                lineHeight: 1.2,
              }}
            >
              {description}
            </small>
          )}
        </div>
      </components.MultiValueLabel>
    );
  };

  return (
    <Select
      className={className}
      styles={customStyles}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isLoading={loading}
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      menuPortalTarget={document.body}
      options={options}
      value={selectedOptions}
      onChange={(opts) => onChange((opts || []).map((o) => o.value))}
      components={{ Option, MultiValueLabel }}
      noOptionsMessage={() => "Sin resultados"}
      /**
       * Búsqueda local:
       * - Por nombre de rol (label)
       * - Por descripción del rol
       */
      filterOption={(option, rawInput) => {
        if (!rawInput) return true;
        const texto = rawInput.toLowerCase();
        const label = (option.label || "").toLowerCase();
        const desc = (option.data.raw?.description || "").toLowerCase();
        return label.includes(texto) || desc.includes(texto);
      }}
    />
  );
};

export default SelectRolesMulti;
